import Toggle from "app/components/Toggle";
import { useEffect, useMemo, useState } from "react";
import Button from "app/storybookComponents/Button";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  clearPendingTeamMemberships,
  getPendingTeamMemberships,
  selectAiFeaturesEnabled,
  selectBulkUpdatePendingTeamMembershipStatus,
  selectCompanySettings,
  selectIsDepartmentsHidden,
  selectPendingTeamMemberships,
  selectPendingTeamMemberStatus,
  selectUpdateCompanySettingsStatus,
  selectUpdateCompanyTimezoneStatus,
  toggleAIFeaturesEnabled,
  toggleDisableDepartments,
  updateCompanySettings,
  updateCompanyTimezone,
} from "./slice";
import { Card, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { WHO_CAN_SETTINGS_MAP } from "./constants";
import {
  getDropdownCheck,
  onAcceptOrDeclineAllPendingTeamInvitationsClick,
} from "./helpers";
import { selectAllCompanyUsersById } from "../Global/slice";
import { CompanySettings, GenericAdminNumberOption } from "./types";
import { useDebounceCallback } from "utils/hooks";
import { getNotifications } from "app/components/Notifications/slice";
import TimezoneDropdown from "app/components/Dropdowns/TimezoneDropdown";
import { TIMEZONE_OPTIONS } from "utils/constants";
import WhoCanDoSomethingModal from "./Modals/WhoCanDoSomethingModal";
import AddPendingTeamMembersToTeam from "./Modals/AddPendingTeamMembersToTeam";
import AdminConsoleSettingsModal from "./Modals/AdminConsoleSettingModal";
import UploadPictureModal from "app/components/ImageEditor/UploadPictureModal";

const GeneralAdminConsoleSettings = () => {
  const dispatch = useAppDispatch();
  // ------------------------ Redux State Selectors ------------------------
  const companySettings = useAppSelector(selectCompanySettings);
  const usersInfoById = useAppSelector(selectAllCompanyUsersById);
  const pendingTeamMembersStatus = useAppSelector(
    selectPendingTeamMemberStatus
  );
  const areDepartmentsHidden = useAppSelector(selectIsDepartmentsHidden);
  const updateCompanyTimezoneStatus = useAppSelector(
    selectUpdateCompanyTimezoneStatus
  );
  const updateCompanySettingsStatus = useAppSelector(
    selectUpdateCompanySettingsStatus
  );
  const bulkUpdatePendingTeamMembershipStatus = useAppSelector(
    selectBulkUpdatePendingTeamMembershipStatus
  );
  const pendingTeamMembers = useAppSelector(selectPendingTeamMemberships);
  const areAIFeaturesEnabled = useAppSelector(selectAiFeaturesEnabled);

  // ------------------------ Local State ------------------------
  const [whoCanJoinOrganization, setWhoCanJoinOrganization] = useState(
    companySettings?.emailSetting
  );
  const [whoCanCreateTeams, setWhoCanCreateTeams] = useState<
    GenericAdminNumberOption | undefined
  >(undefined);
  const [whoCanInvite, setWhoCanInvite] = useState<
    GenericAdminNumberOption | undefined
  >(undefined);
  const [hasRequireTeamAcceptance, setHasRequireTeamAcceptance] =
    useState(false);
  const [timezoneSelected, setTimezoneSelected] = useState<{
    label: string;
    tzCode: string;
  } | null>(null);
  const [showAddPendingTeamMemberModal, setShowAddPendingTeamMemberModal] =
    useState(false);
  const [modalShowing, setModalShowing] = useState<
    null | "Edit Domains" | "Who Can Invite" | "Who Can Create"
  >(null);
  const [showEditGlobalHeaderModal, setShowEditGlobalHeaderModal] =
    useState(false);

  // ------------------------ useEffect ------------------------
  useEffect(() => {
    setWhoCanJoinOrganization(companySettings?.emailSetting);
  }, [companySettings?.emailSetting]);

  useEffect(() => {
    setWhoCanCreateTeams(companySettings?.teamCreationAccess);
  }, [companySettings?.teamCreationAccess]);

  useEffect(() => {
    setWhoCanInvite(companySettings?.invitationAccess);
  }, [companySettings?.invitationAccess]);

  useEffect(() => {
    setHasRequireTeamAcceptance(!!companySettings?.requireTeamAcceptance);
  }, [companySettings?.requireTeamAcceptance]);

  useEffect(() => {
    if (companySettings?.timezone) {
      const foundTimezone = TIMEZONE_OPTIONS.find(
        (tz) => tz.tzCode === companySettings?.timezone
      );
      setTimezoneSelected(foundTimezone ?? null);
    }
  }, [companySettings?.timezone]);

  // ------------------------ Constants ------------------------
  const EDIT_KEYS = 8888;
  const companyAccountId = useMemo(
    () => companySettings?.companyAccountId ?? null,
    [companySettings?.companyAccountId]
  );

  // ------------------------ Action handlers ------------------------
  const onWhoCanJoinOrganizationChange = (value: number) => {
    if (!companyAccountId) {
      return;
    }

    switch (value) {
      case 1: {
        // If the value is already set to all, we don't need to make the call to the endpoint.
        if (whoCanJoinOrganization === 1) return;

        setWhoCanJoinOrganization(1);
        return dispatch(
          updateCompanySettings({
            payload: { emailSetting: 1 },
            companyAccountId: companyAccountId,
          })
        );
      }
      case 2: {
        // If the value is already set to onlyCurrentDomain, we don't need to make the call to the endpoint.
        if (whoCanJoinOrganization === 2) return;

        setWhoCanJoinOrganization(2);
        return dispatch(
          updateCompanySettings({
            payload: { emailSetting: 2 },
            companyAccountId: companyAccountId,
          })
        );
      }
      case 3: {
        // If the value is already set to customList, we don't need to make the call to the endpoint.
        setWhoCanJoinOrganization(3);
        if (companySettings?.emailSetting !== 3) {
          dispatch(
            updateCompanySettings({
              payload: { emailSetting: 3 },
              companyAccountId: companyAccountId,
            })
          );
        }
        return;
      }
      case EDIT_KEYS:
        return setModalShowing("Edit Domains");
    }
  };

  const onSaveWhoCanCreateTeams = useDebounceCallback(
    (value: GenericAdminNumberOption) => {
      if (!companyAccountId) {
        return;
      }

      return dispatch(
        updateCompanySettings({
          payload: { teamCreationAccess: value },
          companyAccountId: companyAccountId,
        })
      );
    },
    1000
  );

  const onSaveWhoCanInvite = useDebounceCallback(
    (value: GenericAdminNumberOption) => {
      if (!companyAccountId) {
        return;
      }

      return dispatch(
        updateCompanySettings({
          payload: { invitationAccess: value },
          companyAccountId: companyAccountId,
        })
      );
    },
    1000
  );

  const onAdminConsoleSettingsModalSave = (
    payload: Partial<CompanySettings>
  ) => {
    if (!companyAccountId) {
      return;
    }
    return dispatch(
      updateCompanySettings({
        payload,
        companyAccountId: companyAccountId,
      })
    );
  };

  const onSaveAiFeaturesEnabled = useDebounceCallback((value: boolean) => {
    if (!companyAccountId) {
      return;
    }

    return dispatch(
      updateCompanySettings({
        payload: { aiFeaturesEnabled: Number(value) as 0 | 1 },
        companyAccountId: companyAccountId,
      })
    );
  }, 1000);

  const checkIfValidValue = (
    value: number
  ): value is GenericAdminNumberOption => {
    return value === 1 || value === 2 || value === 3 || value === 4;
  };

  // ------------------------ Get functions ------------------------
  const getCheckOrDiv = (value: number) => {
    return whoCanJoinOrganization === value ? (
      <FontAwesomeIcon icon="check" className="me-2" />
    ) : (
      <div className="me-2" />
    );
  };

  const getWhoCanJoinOrganizationValue = () => {
    switch (whoCanJoinOrganization) {
      case 2:
        return `Anyone with a ${
          companySettings?.originalDomain || "@dmpaper.com"
        } email`;
      case 3:
        return "Custom List";
      case 1:
        return "Anyone";
      default:
        return "Anyone";
    }
  };

  const getEditOrCreateCustomListItem = (customDomainListLength: number) => {
    const icon = customDomainListLength ? "edit" : "plus";
    const editOrCreate = customDomainListLength
      ? "Edit Custom List"
      : "Create Custom List";
    return (
      <Dropdown.Item key={EDIT_KEYS} eventKey={EDIT_KEYS}>
        <FontAwesomeIcon icon={icon} className="me-2" />
        {editOrCreate}
      </Dropdown.Item>
    );
  };

  const getCustomListStringFromIds = (userIds: number[] = []) => {
    if (!userIds.length) return "Custom List (0)";
    const userNames = userIds
      ?.map(
        (userAccountId) =>
          `${usersInfoById[userAccountId]?.firstName} ${usersInfoById[userAccountId]?.lastName}`
      )
      .join(", ");

    return (
      <div>
        <span>Custom List ({userIds.length})</span>
        <span className="d-block">{userNames}</span>
      </div>
    );
  };

  const getWhoCanJoinOrganizationDropdownItems = () => {
    const items = {
      1: "Anyone",
      2: `Anyone with an @${
        companySettings?.originalDomain || "@dmpaper.com"
      } email`,
      3: `Custom List`,
    };
    const customListString =
      companySettings?.customDomainList?.join(", ") ?? null;

    const customListItem = companySettings?.customDomainList?.length ? (
      <div>
        {getCheckOrDiv(3)}
        <span>Custom List ({companySettings?.customDomainList?.length})</span>
        <span className="d-block">({customListString})</span>
      </div>
    ) : null;

    const returnItems = Object.entries(items).map(([value, label]) => {
      if (value === "3" && !customListItem) return null;

      return (
        <Dropdown.Item key={value} eventKey={value}>
          {value === "3" ? (
            customListItem
          ) : (
            <div>
              {getCheckOrDiv(Number.parseInt(value))}
              {label}
            </div>
          )}
        </Dropdown.Item>
      );
    });
    return [
      ...returnItems,
      getEditOrCreateCustomListItem(
        companySettings?.customDomainList?.length ?? 0
      ),
    ];
  };

  const getGenericDropdownItems = (
    itemOrder: number[],
    items: { [value: number]: string },
    selectedValue?: number,
    customUserList: number[] = []
  ) => {
    const dropdownItems = itemOrder.map((key) => {
      const value = items[key];
      if (value === "Custom List" && !customUserList.length) {
        return null;
      }
      return (
        <Dropdown.Item key={key} eventKey={key}>
          <div className="d-flex align-items-center">
            {getDropdownCheck(selectedValue === key)}
            {value === "Custom List"
              ? getCustomListStringFromIds(customUserList)
              : value}
          </div>
        </Dropdown.Item>
      );
    });

    return [
      ...dropdownItems,
      getEditOrCreateCustomListItem(customUserList.length),
    ];
  };

  const getWhoCanJoinOrganizationDropdown = () => (
    <Dropdown
      onSelect={(e) => {
        if (!e) return;
        onWhoCanJoinOrganizationChange(Number.parseInt(e));
      }}
    >
      <Dropdown.Toggle variant="light" id="dropdown-basic">
        {getWhoCanJoinOrganizationValue()}{" "}
        {whoCanJoinOrganization === 3 &&
          `(${companySettings?.customDomainList?.length})`}
      </Dropdown.Toggle>
      <Dropdown.Menu>{getWhoCanJoinOrganizationDropdownItems()}</Dropdown.Menu>
    </Dropdown>
  );

  const getSelectedValue = (storedValue?: number) => {
    if (storedValue === undefined) return "All Members";
    return WHO_CAN_SETTINGS_MAP[storedValue] ?? "All Members";
  };

  const getGenericDropdown = ({
    items,
    onDropdownSelect,
    className,
    customUserList,
    selectedValue,
    itemOrder,
  }: {
    items: { [value: number]: string };
    itemOrder?: number[];
    onDropdownSelect: (payload: number) => void;
    className?: string;
    customUserList?: number[] | null;
    selectedValue?: number;
  }) => {
    if (selectedValue === undefined) {
      return null;
    }
    const orderOfItems = itemOrder || Object.keys(items).map(Number);
    const selectedDropdownOptionString = getSelectedValue(selectedValue);
    return (
      <Dropdown
        className={className}
        onSelect={(e) => {
          if (!e) return;
          onDropdownSelect(Number(e));
        }}
      >
        <Dropdown.Toggle variant="light" id="dropdown-basic">
          {selectedDropdownOptionString}{" "}
          {selectedValue === 4 && `(${customUserList?.length})`}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {getGenericDropdownItems(
            orderOfItems,
            items,
            selectedValue,
            customUserList || []
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const getModals = () => {
    if (!companySettings) {
      return null;
    }
    return (
      <>
        <AdminConsoleSettingsModal
          modalShowing={modalShowing === "Edit Domains" ? "Edit Domains" : null}
          onSave={onAdminConsoleSettingsModalSave}
          hideModal={() => setModalShowing(null)}
          users={usersInfoById}
          companySettings={companySettings || undefined}
        />

        <WhoCanDoSomethingModal
          show={modalShowing === "Who Can Create"}
          onClose={() => setModalShowing(null)}
          title="Who Can Create Teams"
          descriptionText="Enter members in your organization who can create teams"
          defaultUserAccountIds={companySettings?.teamCreationCustomList}
          onSave={(userAccountIds) => {
            if (!companyAccountId) {
              return;
            }
            dispatch(
              updateCompanySettings({
                payload: {
                  teamCreationCustomList: userAccountIds,
                  teamCreationAccess: 4,
                },
                companyAccountId,
              })
            );
          }}
        />
        <WhoCanDoSomethingModal
          show={modalShowing === "Who Can Invite"}
          onClose={() => setModalShowing(null)}
          title="Who Can Invite New Members"
          descriptionText="Select the users who can invite new members to your organization"
          defaultUserAccountIds={companySettings?.invitationCustomList}
          onSave={(userAccountIds) => {
            if (!companyAccountId) {
              return;
            }
            dispatch(
              updateCompanySettings({
                payload: {
                  invitationCustomList: userAccountIds,
                  invitationAccess: 4,
                },
                companyAccountId,
              })
            );
          }}
        />
        <AddPendingTeamMembersToTeam
          show={showAddPendingTeamMemberModal}
          onHide={() => {
            setShowAddPendingTeamMemberModal(false);
            dispatch(clearPendingTeamMemberships());
          }}
          onAccept={() => {
            onAcceptOrDeclineAllPendingTeamInvitationsClick(
              dispatch,
              Number(companyAccountId),
              "accept"
            );
          }}
          onDecline={() => {
            onAcceptOrDeclineAllPendingTeamInvitationsClick(
              dispatch,
              Number(companyAccountId),
              "decline"
            );
          }}
          isLoading={bulkUpdatePendingTeamMembershipStatus === "loading"}
          onRemoveRequiredAcceptance={() => {
            setHasRequireTeamAcceptance(false);
            dispatch(
              updateCompanySettings({
                payload: { requireTeamAcceptance: 0 },
                companyAccountId: Number(companyAccountId),
              })
            );
            dispatch(getNotifications());
          }}
          pendingTeamMembers={pendingTeamMembers}
        />
        <UploadPictureModal
          modalShowing={showEditGlobalHeaderModal ? "coverPhoto" : null}
          closeModal={() => setShowEditGlobalHeaderModal(false)}
          picture={companySettings?.globalHeaderBase64}
          updateGuidePhoto={async (photo, _, imageName) => {
            if (!companyAccountId) {
              return;
            }
            await dispatch(
              updateCompanySettings({
                payload: { globalHeaderImage: photo, globalHeader: imageName },
                companyAccountId,
              })
            );
            setShowEditGlobalHeaderModal(false);
          }}
          deleteGuidePhoto={async () => {
            if (!companyAccountId) {
              return;
            }
            await dispatch(
              updateCompanySettings({
                payload: { deleteGlobalHeader: true },
                companyAccountId,
              })
            );
            setShowEditGlobalHeaderModal(false);
          }}
        />
      </>
    );
  };

  return (
    <>
      {getModals()}
      <Card className="admin-console-settings-card">
        <h2>General Settings</h2>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Who Can Join Your Organization?</h3>
            <p>Specify allowable domains for joining your organization.</p>
          </div>
          <div className="setting-row-action">
            {getWhoCanJoinOrganizationDropdown()}
          </div>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Who Can Create Teams?</h3>
            <p>Specify which users in your organization can create teams.</p>
          </div>
          <div className="setting-row-action">
            {getGenericDropdown({
              items: WHO_CAN_SETTINGS_MAP,
              itemOrder: [1, 3, 2, 4],
              onDropdownSelect: (e) => {
                if (e === EDIT_KEYS) {
                  return setModalShowing("Who Can Create");
                }

                if (checkIfValidValue(e)) {
                  setWhoCanCreateTeams(e);
                  onSaveWhoCanCreateTeams(e);
                }
              },
              selectedValue: whoCanCreateTeams,
              customUserList: companySettings?.teamCreationCustomList,
            })}
          </div>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Who Can Add People To This Organization?</h3>
            <p>
              Specify which users can invite new users by email or using the
              company invite link.
            </p>
          </div>
          <div className="setting-row-action">
            {getGenericDropdown({
              items: WHO_CAN_SETTINGS_MAP,
              itemOrder: [1, 3, 2, 4],
              onDropdownSelect: (e) => {
                if (e === EDIT_KEYS) {
                  return setModalShowing("Who Can Invite");
                }
                if (checkIfValidValue(e)) {
                  setWhoCanInvite(e);
                  onSaveWhoCanInvite(e);
                }
              },
              selectedValue: whoCanInvite,
              customUserList: companySettings?.invitationCustomList,
            })}
          </div>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>
              Allow Admins to Add Users to Teams without Users Accepting
              Invites?
            </h3>
            <p>
              When this is on, admins can add users to teams without users
              accepting invites. When it's off, people have to accept invites to
              be added to a team.
            </p>
          </div>
          <div className="setting-row-action">
            <Toggle
              isOn={!hasRequireTeamAcceptance}
              loading={
                hasRequireTeamAcceptance &&
                pendingTeamMembersStatus === "loading"
              }
              handleToggle={async () => {
                // When turning this setting on, no need to check if there are pending team members
                if (!hasRequireTeamAcceptance) {
                  setHasRequireTeamAcceptance(true);
                  dispatch(
                    updateCompanySettings({
                      payload: { requireTeamAcceptance: 1 },
                      companyAccountId: Number(companyAccountId),
                    })
                  );
                  return;
                }

                // When turning this setting off, check if there are pending team members
                const { payload } = (await dispatch(
                  getPendingTeamMemberships()
                )) as { payload: any[] };

                // If there are pending team members, show the modal to add them to the team or decline them
                if (payload?.length) {
                  return setShowAddPendingTeamMemberModal(true);
                }

                // If there are no pending team members, turn off the setting
                setHasRequireTeamAcceptance(false);
                dispatch(
                  updateCompanySettings({
                    payload: { requireTeamAcceptance: 0 },
                    companyAccountId: Number(companyAccountId),
                  })
                );
                dispatch(getNotifications());
              }}
            />
          </div>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Enable Departments</h3>
            <p>
              Enable admins to create departments and invite department leaders.
              Department leaders can view TEAMscan results for their department.
            </p>
          </div>
          <div className="setting-row-action">
            <Toggle
              isOn={!areDepartmentsHidden}
              handleToggle={() => {
                dispatch(toggleDisableDepartments(!areDepartmentsHidden));
                dispatch(
                  updateCompanySettings({
                    payload: {
                      areDepartmentsHidden: areDepartmentsHidden ? 0 : 1,
                    },
                    companyAccountId: Number(companyAccountId),
                  })
                );
              }}
              disabled={updateCompanySettingsStatus === "loading"}
            />
          </div>
        </div>

        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Time zone</h3>
            <p>{timezoneSelected?.label ?? null}</p>
          </div>
          <div className="setting-row-action">
            <TimezoneDropdown
              selectedValue={timezoneSelected?.label}
              onTzCodeSelect={(tzCode) =>
                dispatch(updateCompanyTimezone(tzCode))
              }
              disabled={updateCompanyTimezoneStatus === "loading"}
            />
          </div>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Enable All AI Features</h3>
            <p>
              Enable AI functionality for Develop. This includes Coach Bo (our
              Coach Bot) as well as AI generated free text summaries for
              organization and department leaders.
            </p>
          </div>
          <div className="setting-row-action">
            <Toggle
              isOn={areAIFeaturesEnabled}
              handleToggle={() => {
                dispatch(toggleAIFeaturesEnabled(!areAIFeaturesEnabled));
                onSaveAiFeaturesEnabled(!areAIFeaturesEnabled);
              }}
            />
          </div>
        </div>
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Default Global Header Image</h3>
            <p>
              This image will be the default header image for all user and team
              profiles in your organization.
            </p>
          </div>
          <div className="setting-row-action">
            <Button
              variant="secondary-blue"
              onClick={() => setShowEditGlobalHeaderModal(true)}
            >
              <FontAwesomeIcon icon="upload" className="me-2" /> Upload
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
};

export default GeneralAdminConsoleSettings;
